import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CHAPTERS,
  buildSteps,
  chapterStatuses,
  getOptions,
  type Answers,
  type AnswerValue,
  type Question,
} from '@gbcmc/survey-core';
import { ProgressBar } from './components/ProgressBar';
import { ChapterNav } from './components/ChapterNav';
import { ChapterBackground } from './components/ChapterBackground';
import { BrandMark } from './components/BrandMark';
import { WelcomeScreen } from './screens/WelcomeScreen';
import { ChapterTransition } from './screens/ChapterTransition';
import { QuestionScreen } from './screens/QuestionScreen';
import { SendScreen } from './screens/SendScreen';
import { ClosingScreen } from './screens/ClosingScreen';
import { ClosedScreen } from './screens/ClosedScreen';
import { getConfig, submitResponses, SurveyClosedError } from './api/client';
import { clearState, loadState, saveState, type Phase } from './lib/storage';

// Valida la respuesta de la pregunta actual. Devuelve mensaje o null.
function validateQuestion(q: Question, answers: Answers): string | null {
  const value = answers[q.id];
  if (q.type === 'open') return null; // nunca obligatoria
  if (q.type === 'matrix') {
    const val = (value as Record<string, string> | undefined) ?? {};
    const missing = q.rows.filter((r) => !val[r]).length;
    if (missing > 0) return `Marca una opción en cada área para continuar (faltan ${missing}).`;
    return null;
  }
  if (q.type === 'single') {
    // Debe haber selección y, para opciones dinámicas, seguir siendo válida
    // (p. ej. el curso tras cambiar el nivel).
    if (typeof value !== 'string' || !getOptions(q, answers).includes(value)) {
      return 'Selecciona una opción para continuar.';
    }
    return null;
  }
  if (value == null || value === '') return 'Selecciona una opción para continuar.';
  return null;
}

// Avance persistido leído una sola vez, de forma síncrona, antes del primer
// render. Evita la condición de carrera entre restaurar y autoguardar.
const bootstrap = loadState();

export function App() {
  const [answers, setAnswers] = useState<Answers>(bootstrap?.answers ?? {});
  const [phase, setPhase] = useState<Phase>(bootstrap?.phase === 'done' ? 'done' : 'welcome');
  const [stepIndex, setStepIndex] = useState(
    bootstrap && bootstrap.phase !== 'done' ? bootstrap.stepIndex : 0,
  );
  const [hasProgress, setHasProgress] = useState(
    !!bootstrap && bootstrap.phase !== 'done' && bootstrap.stepIndex > 0,
  );
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState('');
  const [period, setPeriod] = useState('2026-1');
  const [navOpen, setNavOpen] = useState(false);
  const navToggleRef = useRef<HTMLButtonElement>(null);

  // Consultar la ventana de medición (fail-open: si falla, se permite responder;
  // el cierre real lo impone el backend con 403 al enviar).
  useEffect(() => {
    let active = true;
    getConfig()
      .then((cfg) => {
        if (!active) return;
        setPeriod(cfg.period);
        if (!cfg.open) setPhase((p) => (p === 'done' ? p : 'closed'));
      })
      .catch(() => {
        /* sin config: se asume abierta */
      });
    return () => {
      active = false;
    };
  }, []);

  // Guardar avance en cada cambio relevante. El estado inicial ya refleja lo
  // persistido, así que la primera escritura es idempotente.
  useEffect(() => {
    saveState({ answers, stepIndex, phase });
  }, [answers, stepIndex, phase]);

  const steps = useMemo(() => buildSteps(answers, true), [answers]);
  const safeIndex = Math.min(stepIndex, steps.length - 1);
  const step = steps[safeIndex];
  const statuses = useMemo(() => chapterStatuses(answers), [answers]);

  const closeNav = () => {
    setNavOpen(false);
    navToggleRef.current?.focus(); // devolver el foco al botón disparador
  };

  // Saltar al primer paso de un capítulo (revisar/cambiar respuestas).
  const jumpToChapter = (chapterId: string) => {
    let idx = steps.findIndex((s) => s.chapter.id === chapterId && s.kind === 'question');
    if (idx === -1) idx = steps.findIndex((s) => s.chapter.id === chapterId);
    if (idx === -1) return;
    setError(null);
    setStepIndex(idx);
    setNavOpen(false); // el foco lo toma el encabezado de la pregunta
  };

  const setAnswer = (id: string, v: AnswerValue) => {
    setError(null);
    setAnswers((prev) => ({ ...prev, [id]: v }));
  };

  const goNext = () => {
    if (step.kind === 'question') {
      const err = validateQuestion(step.question, answers);
      if (err) {
        setError(err);
        return;
      }
    }
    setError(null);
    if (safeIndex < steps.length - 1) setStepIndex(safeIndex + 1);
  };

  const goBack = () => {
    setError(null);
    if (safeIndex > 0) setStepIndex(safeIndex - 1);
    else setPhase('welcome');
  };

  const start = () => {
    setAnswers({});
    setStepIndex(0);
    setError(null);
    setSendError(null);
    setHasProgress(false);
    clearState();
    setPhase('survey');
  };

  const resume = () => {
    // stepIndex ya se restauró del estado persistido; solo cambiamos de fase.
    setError(null);
    setPhase('survey');
  };

  const submit = async () => {
    setSending(true);
    setSendError(null);
    try {
      await submitResponses(answers, { period, honeypot });
      setPhase('done');
    } catch (err) {
      if (err instanceof SurveyClosedError) {
        setPhase('closed');
      } else {
        // Fallo de red/servidor tras los reintentos: se conserva todo el estado
        // local y se ofrece reintentar. Nunca se pierden respuestas (§5).
        setSendError(
          'No pudimos enviar tus respuestas. Revisa tu conexión e inténtalo de nuevo; tus respuestas siguen guardadas.',
        );
      }
    } finally {
      setSending(false);
    }
  };

  const liveText =
    phase !== 'survey' || !step
      ? ''
      : step.kind === 'question'
        ? step.question.text
        : step.kind === 'transition'
          ? `Capítulo ${step.chapter.num}: ${step.chapter.title}`
          : '';

  // Marca de agua del escudo (esquina superior derecha). Se omite en la
  // bienvenida (la portada ya trae el escudo y el nombre integrados) y en las
  // transiciones (que lo muestran centrado).
  const showWatermark =
    (phase === 'survey' && step != null && step.kind !== 'transition') ||
    phase === 'done' ||
    phase === 'closed';

  return (
    <div className="gb-app">
      {showWatermark ? <BrandMark className="gb-watermark" /> : null}

      {phase === 'survey' && step ? (
        <ProgressBar
          chapterNum={step.chapter.num}
          totalChapters={CHAPTERS.length}
          chapterLabel={step.chapter.label}
          onToggleNav={() => setNavOpen(true)}
          navToggleRef={navToggleRef}
        />
      ) : null}

      {phase === 'survey' && step ? (
        <ChapterNav
          open={navOpen}
          onClose={closeNav}
          statuses={statuses}
          currentChapterId={step.chapter.id}
          onJump={jumpToChapter}
        />
      ) : null}

      {phase === 'survey' && step.kind === 'question' ? (
        <ChapterBackground chapter={step.chapter} />
      ) : null}

      <div aria-live="polite" className="gb-visually-hidden">
        {liveText}
      </div>

      {phase === 'welcome' ? (
        <WelcomeScreen onStart={start} onResume={resume} hasProgress={hasProgress} />
      ) : null}

      {phase === 'survey' && step.kind === 'transition' ? (
        <ChapterTransition
          chapter={step.chapter}
          isFirst={step.chapter.num === 1}
          onContinue={goNext}
        />
      ) : null}

      {phase === 'survey' && step.kind === 'question' ? (
        <QuestionScreen
          question={step.question}
          chapter={step.chapter}
          qIndex={step.qIndex}
          qTotal={step.qTotal}
          answers={answers}
          setAnswer={setAnswer}
          onBack={goBack}
          onNext={goNext}
          canGoBack
          error={error}
        />
      ) : null}

      {phase === 'survey' && step.kind === 'send' ? (
        <SendScreen
          onSubmit={submit}
          onBack={goBack}
          sending={sending}
          error={sendError}
          honeypot={honeypot}
          setHoneypot={setHoneypot}
        />
      ) : null}

      {phase === 'done' ? <ClosingScreen onRestart={start} /> : null}

      {phase === 'closed' ? <ClosedScreen /> : null}
    </div>
  );
}
